import React, { useState, useEffect } from "react";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Grid from "@material-ui/core/Grid";
import { withSnackbar } from "notistack";
import Scheduler, { Resource } from "devextreme-react/scheduler";
import moment from "moment";
import { connect } from "react-redux";
import LinearProgress from "@material-ui/core/LinearProgress";
import uuidv4 from "uuid/v4";
import Typography from "@material-ui/core/Typography";

import TimeOffRequestDialog from "../TimeOffRequest";
import ScheduleLegend from "../../ScheduleLegend";
import {
  queryShifts,
  createShift,
  deleteShift,
  publishShifts,
  shiftReminders,
} from "../../../utils/appsyncOperations";
import LoadingButton from "../../Buttons/Loading";
import DataCell from "./DataCell.js";
import ConditionalTooltip from "../../ConditionalTooltip";
import { shift as shiftConstants } from "../../../constants";
import ShiftDetails from "../ShiftDetails";
import { isUserRole } from "../../../utils/user";

const views = ["month"];

const styles = {
  progress: {
    height: 4,
    marginBottom: 8,
    marginTop: 8,
  },
};

const messages = {
  copying: "Click on a day to paste.",
  viewing: "Select days to get started.",
  pasing: "Pasting...",
};

const modes = {
  copy: {
    value: "COPY",
  },
  paste: {
    value: "PASTE",
  },
  view: {
    value: "VIEW",
  },
};

const statusData = [
  {
    text: "Published",
    id: "PUBLISHED",
    color: "#cb6bb2",
  },
  {
    text: "Created",
    id: "CREATED",
    color: "#56ca85",
  },
  {
    text: "Acknowledged",
    id: "ACKNOWLEDGED",
    color: "#1e90ff",
  },
  {
    text: "Time off requested",
    id: "TIME_OFF_REQUESTED",
    color: "goldenrod",
  },
  {
    text: "Time off approved",
    id: "TIME_OFF_APPROVED",
    color: "#121212",
  },
  {
    text: "Coverage requested",
    id: "COVERAGE_REQUESTED",
    color: "goldenrod",
  },
  {
    text: "Coverage approved",
    id: "COVERAGE_APPROVED",
    color: "goldenrod",
  },
];

const ShiftCalendar = ({
  isOpen,
  onClose,
  enqueueSnackbar,
  divisionId,
  serviceProviderId,
  users,
  currentUser,
}) => {
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [data, setData] = useState({});
  const [formattedData, setFormattedData] = useState([]);
  const [mode, setMode] = useState(modes.view.value);
  const [selectedAppointments, setSelectedAppointments] = useState({});
  const [selectedDates, setSelectedDates] = useState({});
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [selectedShiftId, setSelectedShiftId] = useState(null);

  useEffect(() => {
    queryData();
  }, [currentDate]);
  const queryData = async () => {
    const currentMoment = moment(currentDate);
    setLoading(true);
    try {
      const to = currentMoment.endOf("month").add(1, "month").valueOf();
      const from = currentMoment
        .startOf("month")
        .subtract(2, "month")
        .valueOf();
      const shifts = await queryShifts({
        serviceProviderId,
        to,
        from,
      });
      const divisionShifts = shifts.filter(
        (shift) => shift.divisionId === divisionId
      );
      setData({
        ...data,
        ...divisionShifts.reduce(
          (acc, shift) => ({ ...acc, [shift.shiftId]: shift }),
          {}
        ),
      });
    } catch (e) {
      enqueueSnackbar(e, { variant: "error" });
    }
    setLoading(false);
  };

  useEffect(() => {
    setFormattedData(
      Object.values(data).map((shift) => ({
        text: shift.userId ? users[shift.userId].displayName : "Open Shift",
        shiftId: shift.shiftId,
        startDate: new Date(shift.startTime),
        endDate: new Date(shift.endTime),
        status: shift.status,
      }))
    );
  }, [data]);

  useEffect(() => {
    let tempAppts = {};

    for (const date of Object.values(selectedDates)) {
      for (const shift of formattedData) {
        if (
          date.startDate.getTime() <= shift.startDate.getTime() &&
          date.endDate.getTime() > shift.startDate.getTime()
        ) {
          tempAppts[shift.shiftId] = shift;
        }
      }
    }
    setSelectedAppointments(tempAppts);
  }, [selectedDates]);

  const handleCopyClick = () => {
    setMode(modes.copy.value);
  };

  const handleDeleteClick = async () => {
    setDeleteLoading(true);
    const promises = [];
    const shifts = { ...data };
    let successes = 0;
    let errors = {};
    for (const shift of Object.values(selectedAppointments)) {
      promises.push(
        new Promise(async (res, rej) => {
          try {
            await deleteShift({
              shiftId: shift.shiftId,
              updatedBy: currentUser,
              serviceProviderId: shifts[shift.shiftId].serviceProviderId,
            });
            delete shifts[shift.shiftId];
            successes++;
            res(shift);
          } catch (e) {
            errors = {
              ...errors,
              [shift.shiftId]: {
                shift,
                error: e,
              },
            };
            res(null);
            console.error(e);
          }
        })
      );
    }

    const results = await Promise.all(promises);
    const numErrors = Object.keys(errors).length;
    const numSuccess = successes;
    if (numErrors > 1) {
      enqueueSnackbar(`${numErrors} shifts could not be deleted.`, {
        variant: "error",
      });
    } else if (numErrors === 1) {
      enqueueSnackbar(`1 shift could not be deleted.`, { variant: "error" });
    }
    if (numSuccess > 1) {
      enqueueSnackbar(`${numSuccess} shifts were deleted.`, {
        variant: "success",
      });
    } else if (numSuccess === 1) {
      enqueueSnackbar(`1 shift was deleted.`, { variant: "success" });
    }
    setData(shifts);
    setMode(modes.view.value);
    setSelectedDates({});
    setDeleteLoading(false);
  };

  const handlePublishClick = async () => {
    setPublishLoading(true);
    const idsToPublish = Object.values(selectedAppointments)
      .filter((shift) => shift.status === shiftConstants.status.created.value)
      .map((shift) => shift.shiftId);
    try {
      const tempData = { ...data };
      const shifts = await publishShifts(idsToPublish);
      for (const shift of shifts) {
        tempData[shift.shiftId] = shift;
      }
      enqueueSnackbar(
        `${shifts.length} shift${shifts.length === 1 ? "" : "s"} published.`
      );
      setData(tempData);
      enqueueSnackbar(
        `${shifts.length} shift${shifts.length > 1 ? "s" : ""} published`,
        { variant: "success" }
      );
    } catch (e) {
      console.error(e);
      enqueueSnackbar(e, { variant: "error" });
    }
    setPublishLoading(false);
    setSelectedDates({});
  };

  const handleRemindersClick = async () => {
    setReminderLoading(true);
    const idsToRemind = Object.values(selectedAppointments)
      .filter((shift) => shift.status === shiftConstants.status.published.value)
      .map((shift) => shift.shiftId);
    try {
      const tempData = { ...data };
      const shifts = await shiftReminders(idsToRemind);
      for (const shift of shifts) {
        tempData[shift.shiftId] = shift;
      }
      enqueueSnackbar(
        `${shifts.length} reminder${shifts.length === 1 ? "" : "s"} sent.`
      );
      setData(tempData);
      enqueueSnackbar(
        `${shifts.length} reminder${shifts.length > 1 ? "s" : ""} sent`,
        { variant: "success" }
      );
    } catch (e) {
      console.error(e);
      enqueueSnackbar(e, { variant: "error" });
    }
    setReminderLoading(false);
    setSelectedDates({});
  };

  const handleClearClick = () => {
    setMode(modes.view.value);
    setSelectedDates({});
  };

  const handleCellClick = async (cellData) => {
    if (isUserRole({ admin: true })) {
      if (mode === modes.view.value) {
        const startTime = cellData.startDate.getTime();
        if (selectedDates[startTime]) {
          const tempDates = { ...selectedDates };
          delete tempDates[startTime];
          setSelectedDates(tempDates);
        } else {
          setSelectedDates({
            ...selectedDates,
            [startTime]: {
              startDate: cellData.startDate,
              endDate: cellData.endDate,
            },
          });
        }
      } else if (mode === modes.copy.value) {
        setLoading(true);
        let earliestShift = null;
        for (const date of Object.keys(selectedDates)) {
          if (!earliestShift || earliestShift > date) {
            earliestShift = parseInt(date);
          }
        }
        const offset =
          cellData.startDate.getTime() -
          moment(earliestShift).startOf("day").valueOf();
        let newShifts = {};
        const promises = [];
        let errors = {};
        for (const shift of Object.values(selectedAppointments)) {
          const startTime = moment(
            shift.startDate.getTime() + offset
          ).valueOf();
          const endTime = moment(shift.endDate.getTime() + offset).valueOf();
          const newShift = {
            ...data[shift.shiftId],
            shiftId: uuidv4(),
            startTime,
            endTime,
          };

          promises.push(
            new Promise(async (res, rej) => {
              try {
                const shift = await createShift(newShift);
                newShifts = {
                  ...newShifts,
                  [shift.shiftId]: shift,
                };
                res(shift);
              } catch (e) {
                errors = {
                  ...errors,
                  [shift.shiftId]: {
                    shift,
                    error: e,
                  },
                };
                res(null);
                console.error(e);
              }
            })
          );
        }

        const results = await Promise.all(promises);
        const numErrors = Object.keys(errors).length;
        const numSuccess = Object.keys(newShifts).length;
        if (numErrors > 1) {
          enqueueSnackbar(`${numErrors} shifts could not be created.`, {
            variant: "error",
          });
        } else if (numErrors === 1) {
          enqueueSnackbar(`1 shift could not be created.`, {
            variant: "error",
          });
        }
        if (numSuccess > 1) {
          enqueueSnackbar(`${numSuccess} shifts were created.`, {
            variant: "success",
          });
        } else if (numSuccess === 1) {
          enqueueSnackbar(`1 shift was created.`, { variant: "success" });
        }
        setData({
          ...data,
          ...newShifts,
        });
        setMode(modes.view.value);
        setSelectedDates({});
        setLoading(false);
      }
    }
  };

  const selectedShift = data[selectedShiftId];
  const timeOffShift =
    selectedShift !== undefined &&
    (selectedShift.status === shiftConstants.status.timeOffRequested.value ||
      selectedShift.status === shiftConstants.status.timeOffApproved.value);

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        fullWidth
        maxWidth="xl"
        scroll="paper"
      >
        <DialogTitle>Calendar view</DialogTitle>
        <DialogContent>
          <Typography variant={"h2"}>
            {mode === modes.view.value ? messages.viewing : messages.copying}
          </Typography>
          <Grid container spacing={2} justify="space-between">
            <Grid item xs={12} s={6}>
              <ScheduleLegend />
            </Grid>
            <Grid item xs={12}>
              <Button onClick={queryData} color="primary">
                Reload
              </Button>
            </Grid>
            <Grid item xs={12} s={6}>
              {loading ? (
                <LinearProgress style={styles.progress} />
              ) : (
                <div style={styles.progress} />
              )}
              <Scheduler
                dataCellComponent={(e) =>
                  DataCell(
                    e,
                    Object.keys(selectedDates),
                    mode === modes.copy.value
                  )
                }
                dataSource={formattedData}
                views={views}
                currentDate={currentDate}
                currentView={"month"}
                showCurrentTimeIndicator
                firstDayOfWeek={0}
                height={600}
                onCellClick={(e) => {
                  handleCellClick(e.cellData);
                }}
                onAppointmentClick={(e) => {
                  setSelectedShiftId(e.appointmentData.shiftId);
                  e.cancel = true;
                }}
                editing={{
                  allowAdding: false,
                  allowDeleting: false,
                  allowDragging: false,
                  allowResizing: false,
                  allowUpdating: false,
                }}
                onAppointmentDblClick={(e) => (e.cancel = true)}
                onOptionChanged={(e) => {
                  if (e.fullName === "currentDate") {
                    setCurrentDate(e.value);
                  }
                }}
              >
                <Resource
                  fieldExpr={"status"}
                  allowMultiple={true}
                  dataSource={statusData}
                  label={"Status"}
                  useColorAsDefault={true}
                />
              </Scheduler>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          {isUserRole({ admin: true }) && (
            <>
              <ConditionalTooltip
                visible={
                  Object.keys(selectedDates).length === 0 ||
                  mode === modes.copy.value
                }
                title={
                  mode === modes.copy.value
                    ? `Click clear to exit copying.`
                    : "Nothing selected."
                }
              >
                <span>
                  <Button
                    disabled={
                      Object.keys(selectedDates).length === 0 ||
                      mode === modes.copy.value
                    }
                    color="primary"
                    onClick={handleCopyClick}
                  >
                    Copy
                  </Button>
                </span>
              </ConditionalTooltip>
              <ConditionalTooltip
                visible={
                  Object.keys(selectedDates).length === 0 ||
                  mode === modes.copy.value
                }
                title={
                  mode === modes.copy.value
                    ? `Can't delete while copying.`
                    : "Nothing selected."
                }
              >
                <span>
                  <LoadingButton
                    isLoading={deleteLoading}
                    disabled={
                      Object.keys(selectedDates).length === 0 ||
                      mode === modes.copy.value
                    }
                    color="primary"
                    onClick={handleDeleteClick}
                  >
                    Delete
                  </LoadingButton>
                </span>
              </ConditionalTooltip>
              <ConditionalTooltip
                visible={
                  Object.keys(selectedDates).length === 0 ||
                  mode === modes.copy.value
                }
                title={
                  mode === modes.copy.value
                    ? `Can't publish while copying.`
                    : "Nothing selected."
                }
              >
                <span>
                  <LoadingButton
                    isLoading={publishLoading}
                    disabled={
                      Object.keys(selectedDates).length === 0 ||
                      mode === modes.copy.value
                    }
                    color="primary"
                    onClick={handlePublishClick}
                  >
                    Publish
                  </LoadingButton>
                </span>
              </ConditionalTooltip>
              <ConditionalTooltip
                visible={
                  Object.keys(selectedDates).length === 0 ||
                  mode === modes.copy.value
                }
                title={
                  mode === modes.copy.value
                    ? `Can't send reminders while copying.`
                    : "Nothing selected."
                }
              >
                <span>
                  <LoadingButton
                    isLoading={reminderLoading}
                    disabled={
                      Object.keys(selectedDates).length === 0 ||
                      mode === modes.copy.value
                    }
                    color="primary"
                    onClick={handleRemindersClick}
                  >
                    Send Reminders
                  </LoadingButton>
                </span>
              </ConditionalTooltip>
              <ConditionalTooltip
                visible={Object.keys(selectedDates).length === 0}
                title="Nothing selected."
              >
                <span>
                  <Button
                    disabled={Object.keys(selectedDates).length === 0}
                    color="primary"
                    onClick={handleClearClick}
                  >
                    Clear
                  </Button>
                </span>
              </ConditionalTooltip>
            </>
          )}
          <Button onClick={onClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {selectedShiftId !== null &&
        (!timeOffShift ? (
          <ShiftDetails
            onDelete={(deletedShift) => {
              const temp = { ...data };
              delete temp[deletedShift.shiftId];
              setData(temp);
              setSelectedShiftId(null);
            }}
            onSave={(savedShift) =>
              setData({ ...data, [savedShift.shiftId]: savedShift })
            }
            onPublish={(publishedShift) => {
              setData({ ...data, [publishedShift.shiftId]: publishedShift });
              setSelectedShiftId(null);
            }}
            onCoverageApproved={(approvedShift) => {
              setData({ ...data, [approvedShift.shiftId]: approvedShift });
              setSelectedShiftId(null);
            }}
            onCoverageDenied={(deniedShift) => {
              setData({ ...data, [deniedShift.shiftId]: deniedShift });
              setSelectedShiftId(null);
            }}
            open={Boolean(selectedShiftId)}
            onClose={() => setSelectedShiftId(null)}
            shift={data[selectedShiftId]}
          />
        ) : (
          <TimeOffRequestDialog
            shift={selectedShift}
            open={selectedShift}
            onApproved={(approvedShift) => {
              setData({ ...data, [approvedShift.shiftId]: approvedShift });
              setSelectedShiftId(null);
            }}
            onDenied={(onDeniedShift) => {
              const temp = { ...data };
              delete temp[onDeniedShift.shiftId];
              setData(temp);
              setSelectedShiftId(null);
            }}
            onDeletion={(deletedShift) => {
              const temp = { ...data };
              delete temp[deletedShift.shiftId];
              setData(temp);
              setSelectedShiftId(null);
            }}
            onClose={() => setSelectedShiftId(null)}
          />
        ))}
    </>
  );
};

const mapStateToProps = (state) => ({
  serviceProviderId: state.auth.user.attributes["custom:service_provider_id"],
  users: state.users.byId,
  currentUser: state.auth.user.username,
});

export default connect(mapStateToProps)(withSnackbar(ShiftCalendar));
